import { TransactionResponse } from '@ethersproject/abstract-provider';
import { defaultAbiCoder } from '@ethersproject/abi';
import erc1155Abi from '@manifoldxyz/typed-contracts/abis/ERC1155Creator.json';
import CreatorContract, { ContractError } from './creatorContract';

export type BurnRedeem1155Calldata = {
  mintToAddress: string;
  creatorContractAddress: string; // BR creator contract
  index: string; // BR index uint256
  amount: number; // number of redeems, meaning like "3 of Burn Redeem #4 please", which could return many NFTs under-the-hood (3 * redeemAmount to be precise)
};

export default class Creator1155Contract extends CreatorContract {
  ABI = erc1155Abi;

  constructor(contractAddress: string) {
    super(contractAddress);
  }

  async getBalanceOf(account: string, tokenId: string): Promise<number> {
    return (
      await this._callWeb3WithServerFallback(
        this.contractAddress,
        erc1155Abi,
        'balanceOf',
        [account, tokenId],
        false,
      )
    ).toNumber();
  }

  async uri(tokenId: string): Promise<string> {
    return (
      await this._callWeb3WithServerFallback(
        this.contractAddress,
        erc1155Abi,
        'uri(uint256)',
        [tokenId],
        false,
      )
    ).toString();
  }

  async transferToExtensionAndBurn(
    account: string,
    tokenId: string,
    extensionContractAddress: string,
    burnAmount: number,
    data: BurnRedeem1155Calldata,
  ): Promise<TransactionResponse | undefined> {
    try {
      // creat the custom extra calldata for the tx
      const abiToEncode = [];
      const parametersToEncode = [];
      abiToEncode.push('address');
      abiToEncode.push('address');
      abiToEncode.push('uint256');
      abiToEncode.push('uint256');
      parametersToEncode.push(data.mintToAddress);
      parametersToEncode.push(data.creatorContractAddress);
      parametersToEncode.push(data.index);
      parametersToEncode.push(data.amount);
      const burnRedeemExtraData = defaultAbiCoder.encode(abiToEncode, parametersToEncode);

      // overestimate gas by 1.25x
      //
      // We have allowlisted calls to `eth_estimateGas` on `safeTransferFrom` signature
      // in manifold provider service. This way, estimation will work in the case
      // of WC provider that cant estimate gas. Otherwise this call  will be made
      // using the browser provider. If all of those fail, we will error.
      const gasEstimate = await this._estimateGas3WithServerFallback(
        this.contractAddress,
        erc1155Abi,
        'safeTransferFrom(address,address,uint256,uint256,bytes)',
        [
          account,
          extensionContractAddress,
          tokenId,
          burnAmount,
          burnRedeemExtraData,
          { from: account },
        ],
      );

      // On WalletConnect provider, you can only make eth_call calls.
      // This means we have to pass in `unchecked` to ethersjs to
      // sidestep the automatic gas estimation that happens before
      // *all* write calls and then pass in our own gas estimate
      // from before, which in WC's case comes from the manifold
      // provider service.
      let unchecked = false;
      if (
        localStorage.getItem('connectMethod') &&
        localStorage.getItem('connectMethod') === 'walletConnect'
      ) {
        unchecked = true;
      }

      return await this._getContractInstance(
        this.contractAddress,
        erc1155Abi,
        true,
        false,
        unchecked,
      ).safeTransferFrom(
        account,
        extensionContractAddress,
        tokenId,
        burnAmount,
        burnRedeemExtraData,
        { gasLimit: gasEstimate },
      );
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }
}
